import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import MenuIcon from '@material-ui/icons/Menu';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import PersonIcon from '@material-ui/icons/Person';
import IntlMessages from 'util/IntlMessages';
import { keaPalette } from 'styles/designTokens';
import { resolveDropdownPreviewBounds } from '../previewSurface';

const PseudoHeader = ({ anchorRef, compact, mobile }) => (
  <Box
    height="60px" bgcolor="#ffffff" borderBottom="1px solid #e0e0e0"
    display="flex" alignItems="center" px={compact ? 2 : 3}
    justifyContent="space-between" color="#9e9e9e"
  >
    {!mobile && (
      <Box display="flex" alignItems="center" flexShrink={0}>
        <Typography variant="h6" style={{ fontWeight: 800, letterSpacing: -1, color: '#bdbdbd' }}>
          <IntlMessages id="ui.searchExperienceV2.preview.shells.dropdownShell.brand" />
        </Typography>
        {!compact && (
          <Box ml={4} display="flex" alignItems="center">
            <Typography variant="body2"><IntlMessages id="ui.searchExperienceV2.preview.shells.dropdownShell.categories" /></Typography>
            <Box width={24} />
            <Typography variant="body2"><IntlMessages id="ui.searchExperienceV2.preview.shells.dropdownShell.deals" /></Typography>
            <Box width={24} />
            <Typography variant="body2"><IntlMessages id="ui.searchExperienceV2.preview.shells.dropdownShell.new" /></Typography>
          </Box>
        )}
      </Box>
    )}

    <Box flex={1} minWidth={0} maxWidth="600px" mx={mobile ? 0 : compact ? 2 : 4}>
      <Box
        ref={anchorRef} data-preview-dropdown-anchor
        width="100%" height="36px" bgcolor="#fff"
        border={`2px solid ${keaPalette.primary}`} borderRadius="4px"
        display="flex" alignItems="center" px={2} color="#000"
        style={{ boxSizing: 'border-box' }}
      >
        <SearchIcon fontSize="small" style={{ color: keaPalette.primary }} />
      </Box>
    </Box>

    {!mobile && (
      <Box display="flex" alignItems="center" flexShrink={0}>
        {!compact && <><PersonIcon /><Box width={16} /></>}
        <ShoppingCartIcon /><Box width={16} /><MenuIcon />
      </Box>
    )}
  </Box>
);

const PseudoContent = () => (
  <>
    <Box height="300px" bgcolor="#eeeeee" display="flex" alignItems="center" justifyContent="center">
      <Typography variant="h3" style={{ opacity: 0.1, fontWeight: 900 }}><IntlMessages id="ui.searchExperienceV2.preview.shells.dropdownShell.heroBanner" /></Typography>
    </Box>
    <Box p={4} display="flex" style={{ gap: 16 }} flexWrap="wrap" justifyContent="center">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Box key={i} width="200px" height="280px" bgcolor="#f5f5f5" borderRadius={2} />
      ))}
    </Box>
  </>
);

/** The real configured canvas, positioned within the measured mock storefront. */
const DropdownShell = ({ children, screen = 'desktop', integration, onSurfaceResize }) => {
  const viewportRef = useRef(null);
  const anchorRef = useRef(null);
  const surfaceRef = useRef(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const [bounds, setBounds] = useState(null);
  const positioning = integration?.positioning;
  const isMobile = screen === 'mobile';

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const anchor = anchorRef.current;
    if (!viewport || !anchor) return undefined;
    const measure = () => {
      const frame = viewport.getBoundingClientRect();
      if (frame.width <= 0 || frame.height <= 0) return;
      const input = anchor.getBoundingClientRect();
      const next = resolveDropdownPreviewBounds(frame, {
        left: input.left - frame.left,
        right: input.right - frame.left,
        bottom: input.bottom - frame.top,
        width: input.width,
      }, positioning);
      setFrameWidth(frame.width);
      setBounds((previous) => (previous && Object.keys(next).every((key) => previous[key] === next[key])
        ? previous : next));
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) {
      observer.observe(viewport);
      observer.observe(anchor);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [positioning, screen]);

  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || !onSurfaceResize) return undefined;
    const measure = () => {
      const { width, height } = surface.getBoundingClientRect();
      if (width > 0) onSurfaceResize({ width, height });
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(surface);
    return () => { if (observer) observer.disconnect(); };
  }, [bounds, onSurfaceResize]);

  return (
    <Box
      ref={viewportRef} data-preview-viewport data-testid="dropdown-preview-viewport"
      width="100%" height="100%" bgcolor="#fff" overflow="hidden" position="relative"
    >
      <PseudoHeader anchorRef={anchorRef} compact={frameWidth < 800} mobile={isMobile} />
      <PseudoContent />
      <Box
        ref={surfaceRef} data-preview-surface="dropdown" data-testid="dropdown-preview-surface"
        position="absolute" zIndex={100} bgcolor="#fff"
        boxShadow="0 4px 20px rgba(0,0,0,0.15)" borderRadius="0 0 8px 8px" overflow="auto"
        style={{
          boxSizing: 'border-box',
          minWidth: 0,
          left: bounds?.left ?? 0,
          top: bounds?.top ?? 60,
          width: bounds?.width ?? '100%',
          maxHeight: bounds?.maxHeight ?? 'calc(100% - 60px)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DropdownShell;
